import {useEffect, useRef} from 'react';
import {useUploadsState} from '../../contexts/UploadsContext';

// Folder counts and upload grids are read through Elasticsearch, which only
// exposes a just-indexed asset after its refresh interval (1s by default).
// A refresh sent at the moment processing finishes can read the old document.
export const UPLOAD_SETTLED_FOLLOW_UP_DELAY = 2500;

const settledCount = batch => (batch.completed || 0) + (batch.failed || 0) + (batch.removed || 0);

// Lives alongside ActionCable. Current upload batches are polled from
// /api/bulk_uploads/:id/status, so they still advance when processing cable
// events are missed or arrive before Elasticsearch shows the processed assets.
// When a batch settles more files, refresh the grid and folder counts, then
// once more after the index has had time to become searchable.
export default function UploadBatchMonitor({scope, onSettled, followUpDelay = UPLOAD_SETTLED_FOLLOW_UP_DELAY}) {
    const {currentBatches = {}, currentBatchScope} = useUploadsState();
    const seen = useRef({scope: null, counts: new Map()});
    const timers = useRef(new Set());
    const onSettledRef = useRef(onSettled);
    onSettledRef.current = onSettled;

    useEffect(() => () => {
        timers.current.forEach(clearTimeout);
        timers.current.clear();
    }, []);

    useEffect(() => {
        const active = scope && currentBatchScope === scope;
        if (seen.current.scope !== (active ? scope : null)) {
            // Batches restored for another organization or user are a baseline,
            // not new progress. Drop follow-ups owned by the previous scope.
            timers.current.forEach(clearTimeout);
            timers.current.clear();
            seen.current = {scope: active ? scope : null, counts: new Map()};
            if (!active) return;
            Object.values(currentBatches).forEach(batch => seen.current.counts.set(String(batch.id), settledCount(batch)));
            return;
        }
        if (!active) return;

        const settled = Object.values(currentBatches).filter(batch => {
            const id = String(batch.id);
            const count = settledCount(batch);
            const previous = seen.current.counts.get(id);
            seen.current.counts.set(id, count);
            return !batch.deleted && count > (previous ?? 0);
        });
        if (!settled.length) return;

        const event = {type: 'uploadBatchSettled', batchIds: settled.map(batch => batch.id)};
        onSettledRef.current?.(event);
        const timer = setTimeout(() => {
            timers.current.delete(timer);
            if (seen.current.scope === scope) onSettledRef.current?.({...event, followUp: true});
        }, followUpDelay);
        timers.current.add(timer);
    }, [currentBatches, currentBatchScope, scope, followUpDelay]);

    return null;
}
